// @ts-nocheck
import { useEffect, useRef, useState, useCallback, memo } from 'react'
import { Trash2, Check, RotateCcw, Edit3, ChevronLeft, ChevronRight, ZoomOut, Pencil, Hand, CheckCircle2 } from 'lucide-react'
import { useAppStore }      from '../../store/useAppStore'
import { useSettingsStore } from '../../store/useSettingsStore'
import { useDamagesStore }  from '../../store/useDamagesStore'
import Button from '../ui/Button'

// ── Constantes ────────────────────────────────────────────────────────────────
const STROKE        = '#EF4444'
const STROKE_W      = 3
const HIT_R         = 22
const HANDLE_R      = 14
const MIN_RX        = 14
const MIN_RY        = 10
const MIN_DRAW_DIST = 28
const ZOOM_MIN      = 1.0
const ZOOM_MAX      = 5.0
const FONT_BADGE    = 'bold 13px sans-serif'
const FONT_HANDLE   = 'bold 14px sans-serif'


// ── Hit test ──────────────────────────────────────────────────────────────────
function hitTest(pos, damages) {
  for (let i = damages.length - 1; i >= 0; i--) {
    const d  = damages[i]
    const rx = Math.abs(d.rx), ry = Math.abs(d.ry)
    const hx = d.x + rx * 0.707, hy = d.y + ry * 0.707
    if (Math.hypot(pos.x - hx, pos.y - hy) <= HANDLE_R + 6)
      return { type: 'handle', damage: d }
    const nx = (pos.x - d.x) / (rx + HIT_R)
    const ny = (pos.y - d.y) / (ry + HIT_R)
    if (nx * nx + ny * ny <= 1)
      return { type: 'ellipse', damage: d }
  }
  return null
}

// ── Dessin ellipse dommage ────────────────────────────────────────────────────
function drawOne(ctx, d, active = false, isPreview = false) {
  const rx = Math.abs(d.rx), ry = Math.abs(d.ry)
  if (rx < 3 || ry < 3) return
  ctx.save()
  ctx.beginPath()
  ctx.ellipse(d.x, d.y, rx, ry, 0, 0, Math.PI * 2)
  ctx.strokeStyle = isPreview ? 'rgba(255,210,0,0.85)' : active ? '#FB923C' : STROKE
  ctx.lineWidth   = active ? 4 : STROKE_W
  if (isPreview) ctx.setLineDash([7, 4])
  ctx.stroke()
  ctx.setLineDash([])
  if (!isPreview) {
    ctx.fillStyle = active ? 'rgba(251,146,60,0.12)' : 'rgba(239,68,68,0.10)'
    ctx.fill()
    const bx = d.x + rx, by = d.y - ry
    ctx.beginPath(); ctx.arc(bx, by, 15, 0, Math.PI * 2)
    ctx.fillStyle = active ? '#FB923C' : STROKE; ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = FONT_BADGE
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillText(String(d.id), bx, by)
    const hx = d.x + rx * 0.707, hy = d.y + ry * 0.707
    ctx.beginPath(); ctx.arc(hx, hy, HANDLE_R, 0, Math.PI * 2)
    ctx.fillStyle = '#fff'; ctx.fill()
    ctx.strokeStyle = active ? '#FB923C' : STROKE; ctx.lineWidth = 2.5; ctx.stroke()
    ctx.fillStyle = active ? '#FB923C' : STROKE
    ctx.font = FONT_HANDLE
    ctx.fillText('⤡', hx, hy + 1)
  }
  ctx.restore()
}

// ── Coordonnées écran → canvas ────────────────────────────────────────────────
function screenToCanvas(clientX, clientY, canvas, view) {
  const rect   = canvas.getBoundingClientRect()
  const cssX   = (clientX - rect.left) / view.zoom
  const cssY   = (clientY - rect.top)  / view.zoom
  const scaleX = canvas.width  / canvas.clientWidth
  const scaleY = canvas.height / canvas.clientHeight
  return { x: cssX * scaleX, y: cssY * scaleY }
}

function getTouchDist(t1, t2) {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
}
function getTouchMid(t1, t2) {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  }
}

// ── Composant principal ───────────────────────────────────────────────────────
export interface AnnotationCanvasProps {
  imageDataUrl: string | null;
  slotId: string;
  slotLabel: string;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onValidate: (annotatedDataUrl: string | null) => void;
  onRetake: () => void;
}

export default memo(function AnnotationCanvas({
  imageDataUrl, slotId, slotLabel,
  onValidate, onRetake,
  onPrev, onNext, hasPrev, hasNext,
}: AnnotationCanvasProps) {
  const canvasRef    = useRef(null)
  const containerRef = useRef(null)
  const imgRef       = useRef(null)
  const viewRef      = useRef({ zoom: 1, panX: 0, panY: 0 })
  const rafRef       = useRef(null)

  const stateRef = useRef({
    damages:         [],
    mode:            null,
    startPos:        null,
    draggingId:      null,
    dragOffset:      { x: 0, y: 0 },
    pinchDist:       null,
    pinchMid:        null,
    pinchZoom0:      1,
    pinchPan0:       { x: 0, y: 0 },
    activePointerId: null,
  })
  const nextIdRef = useRef(1)

  const [damages,    setDamages]    = useState([])
  const [editingId,  setEditingId]  = useState(null)
  const [labelInput, setLabelInput] = useState('')
  const [imgReady,   setImgReady]   = useState(false)
  const [zoomLevel,  setZoomLevel]  = useState(1)
  const [drawMode,   setDrawMode]   = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showHint,    setShowHint]    = useState(true)
  const [flash,       setFlash]       = useState<'left' | 'right' | null>(null)

  const handlePrevClick = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate(40)
    setFlash('left')
    setTimeout(() => setFlash(null), 150)
    onPrev()
  }, [onPrev])

  const handleNextClick = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate(40)
    setFlash('right')
    setTimeout(() => setFlash(null), 150)
    onNext()
  }, [onNext])

  const addDamage  = useAppStore(s => s.addDamage)
  // ✅ Lecture qualité export depuis settings backoffice
  const getSetting = useSettingsStore(s => s.getSetting)
  const predefinedDamages = useDamagesStore(s => s.damages)

  // ── Bloquer scroll Android/iOS + verrouiller orientation ─────────────────
  useEffect(() => {
    const prevent = (e) => {
      if (e.target.closest('button, input, a, [data-allow-touch]')) return
      e.preventDefault()
    }
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width:    document.body.style.width,
      height:   document.body.style.height,
    }
    Object.assign(document.body.style, {
      overflow: 'hidden', position: 'fixed', width: '100%', height: '100%',
    })
    document.addEventListener('touchmove', prevent, { passive: false })
    // ✅ FIX — Verrouiller en portrait pour éviter les freezes lors de la rotation
    try { screen.orientation?.lock?.('portrait').catch(() => {}) } catch {}
    return () => {
      Object.assign(document.body.style, prev)
      document.removeEventListener('touchmove', prevent)
      try { screen.orientation?.unlock?.() } catch {}
    }
  }, [])

  // ── Fit canvas au ratio exact de l'image ──────────────────────────────────
  const fitCanvasToContainer = useCallback(() => {
    const img       = imgRef.current
    const container = containerRef.current
    const canvas    = canvasRef.current
    if (!img || !container || !canvas) return
    const containerW = container.clientWidth
    const containerH = container.clientHeight
    const imgRatio   = img.naturalWidth / img.naturalHeight
    let displayW = containerW
    let displayH = containerW / imgRatio
    if (displayH > containerH) {
      displayH = containerH
      displayW = containerH * imgRatio
    }
    canvas.style.width    = `${displayW}px`
    canvas.style.height   = `${displayH}px`
    canvas.style.position = 'absolute'
    canvas.style.left     = `${Math.round((containerW - displayW) / 2)}px`
    canvas.style.top      = `${Math.round((containerH - displayH) / 2)}px`
  }, [])

  const applyTransform = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const { zoom, panX, panY } = viewRef.current
    canvas.style.transformOrigin = '0 0'
    canvas.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`
  }, [])

  const clampPan = useCallback((z, px, py) => {
    const container = containerRef.current
    const canvas    = canvasRef.current
    if (!container || !canvas) return { x: px, y: py }
    const cw       = container.clientWidth
    const ch       = container.clientHeight
    const displayW = canvas.clientWidth  * z
    const displayH = canvas.clientHeight * z
    return {
      x: Math.min(0, Math.max(cw - displayW, px)),
      y: Math.min(0, Math.max(ch - displayH, py)),
    }
  }, [])

  const resetZoom = useCallback(() => {
    viewRef.current = { zoom: 1, panX: 0, panY: 0 }
    applyTransform()
    setZoomLevel(1)
    setDrawMode(false)
  }, [applyTransform])

  // ── Redraw via RAF ────────────────────────────────────────────────────────
  const redraw = useCallback((dmgs, preview = null) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current
      const img    = imgRef.current
      if (!canvas || !img) return
      const ctx = canvas.getContext('2d', { willReadFrequently: false })
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      dmgs.forEach(d => drawOne(ctx, d, d.id === stateRef.current.draggingId))
      if (preview) drawOne(ctx, preview, false, true)
    })
  }, [])

  // ── Chargement image ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!imageDataUrl) return
    setImgReady(false)
    stateRef.current = {
      damages: [], mode: null, startPos: null,
      draggingId: null, dragOffset: { x: 0, y: 0 },
      pinchDist: null, pinchMid: null, pinchZoom0: 1, pinchPan0: { x: 0, y: 0 },
      activePointerId: null,
    }
    setDamages([])
    setEditingId(null)
    setDrawMode(false)
    setShowHint(true)
    nextIdRef.current = 1
    resetZoom()
    const img = new window.Image()
    img.onload = () => {
      imgRef.current = img
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.width  = img.naturalWidth
      canvas.height = img.naturalHeight
      fitCanvasToContainer()
      redraw([])
      setImgReady(true)
    }
    img.onerror = () => console.error('[AnnotationCanvas] Échec chargement image')
    img.src = imageDataUrl
  }, [imageDataUrl, redraw, resetZoom, fitCanvasToContainer])

  // ── Recalcul si rotation écran (debounced pour éviter cascade) ────────────
  useEffect(() => {
    if (!imgReady) return
    let resizeTimer = null
    const observer = new ResizeObserver(() => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        fitCanvasToContainer()
        // ✅ FIX — ne pas resetZoom si un dessin/drag/export est en cours
        if (!stateRef.current.mode && !isExporting) {
          resetZoom()
        }
      }, 300)
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      observer.disconnect()
    }
  }, [imgReady, fitCanvasToContainer, resetZoom, isExporting])

  // ── Touch Start ───────────────────────────────────────────────────────────
  const onTouchStart = useCallback((e) => {
    setShowHint(false)
    const canvas = canvasRef.current
    if (!canvas) return
    const st = stateRef.current
    const v  = viewRef.current
    if (e.touches.length === 2) {
      e.preventDefault()
      st.mode       = 'pinching'
      st.startPos   = null
      st.pinchDist  = getTouchDist(e.touches[0], e.touches[1])
      st.pinchMid   = getTouchMid(e.touches[0], e.touches[1])
      st.pinchZoom0 = v.zoom
      st.pinchPan0  = { x: v.panX, y: v.panY }
      return
    }
    const t   = e.touches[0]
    const pos = screenToCanvas(t.clientX, t.clientY, canvas, v)
    const hit = hitTest(pos, st.damages)
    if (hit?.type === 'handle') {
      st.mode = 'resizing'; st.draggingId = hit.damage.id
    } else if (hit?.type === 'ellipse') {
      st.mode = 'dragging'; st.draggingId = hit.damage.id
      st.dragOffset = { x: pos.x - hit.damage.x, y: pos.y - hit.damage.y }
    } else if (v.zoom > 1.05 && !drawMode) {
      st.mode      = 'panning'
      st.startPos  = { x: t.clientX, y: t.clientY }
      st.pinchPan0 = { x: v.panX, y: v.panY }
    } else {
      st.mode       = 'drawing'
      st.startPos   = pos
      st.draggingId = null
    }
  }, [drawMode])

  // ── Touch Move ────────────────────────────────────────────────────────────
  const onTouchMove = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const st = stateRef.current
    const v  = viewRef.current
    if (st.mode === 'pinching' && e.touches.length === 2) {
      e.preventDefault()
      const newDist     = getTouchDist(e.touches[0], e.touches[1])
      const rawScale    = newDist / st.pinchDist
      const smoothScale = 1 + (rawScale - 1) * 0.6
      const newZoom     = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, st.pinchZoom0 * smoothScale))
      const rect        = canvas.getBoundingClientRect()
      const ox          = (st.pinchMid.x - rect.left) / st.pinchZoom0
      const oy          = (st.pinchMid.y - rect.top)  / st.pinchZoom0
      const newPanX     = st.pinchPan0.x - ox * (newZoom - st.pinchZoom0)
      const newPanY     = st.pinchPan0.y - oy * (newZoom - st.pinchZoom0)
      const clamped     = clampPan(newZoom, newPanX, newPanY)
      viewRef.current   = { zoom: newZoom, panX: clamped.x, panY: clamped.y }
      applyTransform()
      setZoomLevel(Math.round(newZoom * 10) / 10)
      return
    }
    if (st.mode === 'panning' && e.touches.length === 1) {
      const t       = e.touches[0]
      const clamped = clampPan(
        v.zoom,
        st.pinchPan0.x + (t.clientX - st.startPos.x),
        st.pinchPan0.y + (t.clientY - st.startPos.y)
      )
      viewRef.current.panX = clamped.x
      viewRef.current.panY = clamped.y
      applyTransform()
      return
    }
    const t = e.touches?.[0]
    if (!t) return
    const pos = screenToCanvas(t.clientX, t.clientY, canvas, v)
    if (st.mode === 'dragging' && st.draggingId !== null) {
      const updated = st.damages.map(d =>
        d.id === st.draggingId
          ? { ...d, x: pos.x - st.dragOffset.x, y: pos.y - st.dragOffset.y }
          : d
      )
      st.damages = updated; setDamages([...updated]); redraw(updated)
    } else if (st.mode === 'resizing' && st.draggingId !== null) {
      const updated = st.damages.map(d =>
        d.id !== st.draggingId ? d : {
          ...d,
          rx: Math.max(MIN_RX, Math.abs(pos.x - d.x) / 0.707),
          ry: Math.max(MIN_RY, Math.abs(pos.y - d.y) / 0.707),
        }
      )
      st.damages = updated; setDamages([...updated]); redraw(updated)
    } else if (st.mode === 'drawing' && st.startPos) {
      redraw(st.damages, {
        id: 0,
        x:  (st.startPos.x + pos.x) / 2,
        y:  (st.startPos.y + pos.y) / 2,
        rx: Math.abs(pos.x - st.startPos.x) / 2,
        ry: Math.abs(pos.y - st.startPos.y) / 2,
      })
    }
  }, [redraw, applyTransform, clampPan])

  // ── Touch End ─────────────────────────────────────────────────────────────
  const onTouchEnd = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const st = stateRef.current
    const v  = viewRef.current
    if (st.mode === 'pinching') {
      st.mode = null
      if (v.zoom <= 1.05) resetZoom()
      return
    }
    if (st.mode === 'panning') { st.mode = null; return }
    const t = e.changedTouches?.[0]
    if (!t) return
    const pos = screenToCanvas(t.clientX, t.clientY, canvas, v)
    if (st.mode === 'dragging' || st.mode === 'resizing') {
      st.draggingId = null; st.mode = null; redraw(st.damages)
    } else if (st.mode === 'drawing' && st.startPos) {
      const rx = Math.abs(pos.x - st.startPos.x) / 2
      const ry = Math.abs(pos.y - st.startPos.y) / 2
      if (rx >= MIN_DRAW_DIST && ry >= MIN_DRAW_DIST) {
        const newD = {
          id: nextIdRef.current++,
          x:  (st.startPos.x + pos.x) / 2,
          y:  (st.startPos.y + pos.y) / 2,
          rx, ry, label: '',
        }
        const updated = [...st.damages, newD]
        st.damages = updated; setDamages([...updated]); redraw(updated)
        setTimeout(() => setEditingId(newD.id), 80)
      } else {
        redraw(st.damages)
      }
      st.startPos = null; st.mode = null
    }
  }, [redraw, resetZoom])

  // ── Pointer Events (mouse + stylet iPad) ──────────────────────────────────
  const onPointerDown = useCallback((e) => {
    if (e.pointerType === 'touch') return
    setShowHint(false)
    const canvas = canvasRef.current
    if (!canvas) return
    const v   = viewRef.current
    const pos = screenToCanvas(e.clientX, e.clientY, canvas, v)
    const st  = stateRef.current
    const hit = hitTest(pos, st.damages)
    if (hit?.type === 'handle') {
      st.mode = 'resizing'; st.draggingId = hit.damage.id
    } else if (hit?.type === 'ellipse') {
      st.mode = 'dragging'; st.draggingId = hit.damage.id
      st.dragOffset = { x: pos.x - hit.damage.x, y: pos.y - hit.damage.y }
    } else {
      st.mode = 'drawing'; st.startPos = pos; st.draggingId = null
    }
    canvas.setPointerCapture(e.pointerId)
    st.activePointerId = e.pointerId
  }, [])

  const onPointerMove = useCallback((e) => {
    if (e.pointerType === 'touch') return
    const canvas = canvasRef.current
    if (!canvas) return
    const v   = viewRef.current
    const pos = screenToCanvas(e.clientX, e.clientY, canvas, v)
    const st  = stateRef.current
    if (st.mode === 'dragging' && st.draggingId !== null) {
      const updated = st.damages.map(d =>
        d.id === st.draggingId
          ? { ...d, x: pos.x - st.dragOffset.x, y: pos.y - st.dragOffset.y }
          : d
      )
      st.damages = updated; setDamages([...updated]); redraw(updated)
    } else if (st.mode === 'resizing' && st.draggingId !== null) {
      const updated = st.damages.map(d =>
        d.id !== st.draggingId ? d : {
          ...d,
          rx: Math.max(MIN_RX, Math.abs(pos.x - d.x) / 0.707),
          ry: Math.max(MIN_RY, Math.abs(pos.y - d.y) / 0.707),
        }
      )
      st.damages = updated; setDamages([...updated]); redraw(updated)
    } else if (st.mode === 'drawing' && st.startPos) {
      redraw(st.damages, {
        id: 0,
        x:  (st.startPos.x + pos.x) / 2,
        y:  (st.startPos.y + pos.y) / 2,
        rx: Math.abs(pos.x - st.startPos.x) / 2,
        ry: Math.abs(pos.y - st.startPos.y) / 2,
      })
    }
  }, [redraw])

  const onPointerUp = useCallback((e) => {
    if (e.pointerType === 'touch') return
    const canvas = canvasRef.current
    if (!canvas) return
    const v   = viewRef.current
    const pos = screenToCanvas(e.clientX, e.clientY, canvas, v)
    const st  = stateRef.current
    if (st.mode === 'dragging' || st.mode === 'resizing') {
      st.draggingId = null; st.mode = null; redraw(st.damages)
    } else if (st.mode === 'drawing' && st.startPos) {
      const rx = Math.abs(pos.x - st.startPos.x) / 2
      const ry = Math.abs(pos.y - st.startPos.y) / 2
      if (rx >= MIN_DRAW_DIST && ry >= MIN_DRAW_DIST) {
        const newD = {
          id: nextIdRef.current++,
          x:  (st.startPos.x + pos.x) / 2,
          y:  (st.startPos.y + pos.y) / 2,
          rx, ry, label: '',
        }
        const updated = [...st.damages, newD]
        st.damages = updated; setDamages([...updated]); redraw(updated)
        setTimeout(() => setEditingId(newD.id), 80)
      } else {
        redraw(st.damages)
      }
      st.startPos = null; st.mode = null
    }
    st.activePointerId = null
  }, [redraw])

  // ── Molette souris ────────────────────────────────────────────────────────
  const onWheel = useCallback((e) => {
    setShowHint(false)
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const v       = viewRef.current
    const factor  = e.deltaY < 0 ? 1.15 : 0.87
    const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * factor))
    const rect    = canvas.getBoundingClientRect()
    const ox      = (e.clientX - rect.left) / v.zoom
    const oy      = (e.clientY - rect.top)  / v.zoom
    const newPanX = v.panX - ox * (newZoom - v.zoom)
    const newPanY = v.panY - oy * (newZoom - v.zoom)
    const clamped = clampPan(newZoom, newPanX, newPanY)
    viewRef.current = { zoom: newZoom, panX: clamped.x, panY: clamped.y }
    applyTransform()
    setZoomLevel(Math.round(newZoom * 10) / 10)
  }, [applyTransform, clampPan])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.addEventListener('wheel', onWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', onWheel)
  }, [onWheel])

  // ── Label dommage ─────────────────────────────────────────────────────────
  const saveLabel = useCallback(() => {
    const updated = stateRef.current.damages.map(d =>
      d.id === editingId ? { ...d, label: labelInput } : d
    )
    stateRef.current.damages = updated
    setDamages([...updated])
    setEditingId(null)
    setLabelInput('')
    redraw(updated)
  }, [editingId, labelInput, redraw])

  const removeLocalDamage = useCallback((id) => {
    const updated = stateRef.current.damages.filter(d => d.id !== id)
    stateRef.current.damages = updated
    setDamages([...updated])
    setEditingId(null)
    redraw(updated)
  }, [redraw])

  // ── Validation ────────────────────────────────────────────────────────────
  const handleValidate = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || isExporting) return
    setIsExporting(true)

    const cw = canvas.width  || 1920
    const ch = canvas.height || 1080

    stateRef.current.damages.forEach(d =>
      addDamage({ ...d, slotId, canvasW: cw, canvasH: ch })
    )

    // ✅ FIX — setTimeout(0) pour relâcher le thread UI avant le lourd toDataURL
    setTimeout(() => {
      try {
        const exportQuality = getSetting('photo_supabase_quality')  // défaut 0.65
        const exportCanvas  = document.createElement('canvas')
        exportCanvas.width  = cw
        exportCanvas.height = ch
        const exportCtx = exportCanvas.getContext('2d')
        exportCtx.drawImage(canvas, 0, 0, cw, ch)
        const dataUrl = exportCanvas.toDataURL('image/jpeg', exportQuality)
        console.log(`[export] ${cw}x${ch} — qualité ${exportQuality} — ${(dataUrl.length * 0.75 / 1024 / 1024).toFixed(2)} Mo`)
        exportCanvas.width = exportCanvas.height = 0

        onValidate(dataUrl)

        requestAnimationFrame(() => {
          if (canvasRef.current) {
            canvasRef.current.width  = 0
            canvasRef.current.height = 0
          }
        })
      } catch (err) {
        console.error('[export] Erreur export photo:', err)
      } finally {
        setIsExporting(false)
      }
    }, 0)
  }, [addDamage, slotId, onValidate, getSetting, isExporting])

  // ── Render ────────────────────────────────────────────────────────────────
  const isZoomed = zoomLevel > 1.05

  return (
    <div
      className="flex flex-col bg-black"
      style={{
        height: '100dvh', overflow: 'hidden',
        touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
        position: 'fixed', inset: 0,
      }}
    >
      {/* Zone image */}
      <div
        ref={containerRef}
        className="flex-1 relative"
        style={{ minHeight: 0, overflow: 'hidden' }}
      >
        {/* Flash Overlay */}
        {flash === 'left' && <div className="absolute inset-y-0 left-0 w-1/3 bg-white/30 z-50 pointer-events-none transition-opacity" />}
        {flash === 'right' && <div className="absolute inset-y-0 right-0 w-1/3 bg-white/30 z-50 pointer-events-none transition-opacity" />}

        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            cursor: isZoomed && !drawMode ? 'grab' : 'crosshair',
            touchAction: 'none', WebkitTouchCallout: 'none',
            transformOrigin: '0 0',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        />

        {/* Indicateur zoom + toggle mode */}
        {isZoomed && (
          <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-20">
            <div className="bg-black/70 text-white text-xs font-bold px-2.5 py-1.5 rounded-xl text-center">
              ×{zoomLevel.toFixed(1)}
            </div>
            <button
              onClick={() => setDrawMode(m => !m)}
              className={`text-xs px-2.5 py-1.5 rounded-xl flex items-center gap-1 justify-center font-semibold transition-colors ${
                drawMode ? 'bg-red-500 text-white' : 'bg-black/70 text-white'
              }`}
            >
              {drawMode ? <><Pencil size={12}/> Dessin</> : <><Hand size={12}/> Pan</>}
            </button>
            <button
              onClick={resetZoom}
              className="bg-black/70 text-white text-xs px-2.5 py-1.5 rounded-xl flex items-center gap-1 justify-center"
            >
              <ZoomOut size={12}/> Reset
            </button>
          </div>
        )}

        {/* Hint contextuel */}
        {imgReady && damages.length === 0 && showHint && (
          <div className="absolute inset-x-0 top-10 flex justify-center pointer-events-none z-10 px-6">
            <div className="bg-black/80 text-white text-center px-5 py-3 rounded-2xl shadow-xl border border-white/10 max-w-sm">
              {isZoomed ? (
                <>
                  <p className="font-semibold text-sm">
                    {drawMode ? '✏️ Mode dessin actif' : '🖐 Mode déplacement'}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {drawMode
                      ? 'Tracez une ellipse · Bouton 🖐 pour déplacer'
                      : '1 doigt = déplacer · Bouton ✏️ pour dessiner'}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-sm">Tracez une ellipse sur un dommage</p>
                  <p className="text-gray-400 text-xs mt-1">
                    Pincez pour zoomer · Glissez pour déplacer · ⤡ pour redimensionner
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal label dommage */}
      {editingId !== null && (
        <div
          className="absolute inset-0 bg-black/60 flex items-center justify-center z-[100] p-4"
          style={{ touchAction: 'auto' }}
        >
          <div className="bg-white rounded-2xl p-5 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto flex flex-col">
            <h3 className="font-bold text-gray-800 mb-1">Détail du dommage</h3>
            <p className="text-gray-500 text-sm mb-4">{slotLabel}</p>

            {/* Grille de sélection rapide */}
            <div className="flex flex-wrap gap-2 mb-4">
              {predefinedDamages.map(d => {
                const type = d.label
                const currentParts = labelInput.split(' - ').map(s => s.trim()).filter(Boolean)
                // "Pièces cassés" est long, on recherche une correspondance exacte ou très proche
                const isSelected = currentParts.includes(type)
                
                return (
                  <button
                    key={type}
                    data-allow-touch
                    onClick={(e) => {
                      // e.preventDefault(); e.stopPropagation() — Inutile grâce à data-allow-touch
                      let parts = labelInput.split(' - ').map(s => s.trim()).filter(Boolean)
                      if (parts.includes(type)) {
                        parts = parts.filter(p => p !== type)
                      } else {
                        parts.push(type)
                      }
                      setLabelInput(parts.join(' - '))
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border text-left transition-colors flex items-center gap-2 ${
                      isSelected 
                        ? 'bg-blue-500 text-white border-blue-500' 
                        : 'bg-gray-50 text-gray-700 border-gray-200 active:bg-gray-200'
                    }`}
                  >
                    {isSelected && <CheckCircle2 size={14} className="flex-shrink-0" />}
                    <span>{type}</span>
                  </button>
                )
              })}
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notes complémentaires</label>
              <input
                type="text"
                value={labelInput}
                onChange={e => setLabelInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveLabel()}
                placeholder="Ex : éclat au centre, trace de peinture..."
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-3" data-allow-touch>
              <Button
                data-allow-touch
                onClick={() => { setEditingId(null); setLabelInput('') }}
                variant="ghost"
                className="flex-1"
              >
                Passer
              </Button>
              <Button data-allow-touch onClick={saveLabel} className="flex-1">
                Valider
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Barre du bas — Mode Tableau de Bord Industriel */}
      <div
        className="bg-gray-900 flex items-stretch flex-shrink-0 border-t border-gray-800"
        style={{
          touchAction: 'auto',
          position: 'relative',
          zIndex: 10,
          paddingBottom: 'max(0px, env(safe-area-inset-bottom))',
        }}
      >
        {/* Précédent (Massif) */}
        <button
          onClick={handlePrevClick}
          disabled={!hasPrev}
          className="min-w-[64px] sm:min-w-[80px] bg-amber-400 text-black flex items-center justify-center disabled:bg-gray-800 disabled:text-gray-600 active:bg-amber-300 transition-colors"
        >
          {hasPrev ? <ChevronLeft size={36} strokeWidth={3} /> : <div className="w-8 h-1 bg-gray-600 rounded-full" />}
        </button>

        {/* Espace central (Dommages, Retake, Valider) */}
        <div className="flex-1 flex items-center gap-2 px-2 py-2 overflow-x-auto min-w-0">
          <div className="flex-1 flex gap-1.5 min-w-0">
            {damages.length === 0 ? (
              <span className="text-gray-500 text-xs italic whitespace-nowrap self-center px-2">
                Aucun dommage
              </span>
            ) : (
              damages.map(d => (
                <div
                  key={d.id}
                  className="flex items-center gap-1 bg-red-950 border border-red-800 text-red-200 rounded-full px-2.5 py-1 text-xs whitespace-nowrap flex-shrink-0"
                >
                  <span className="font-bold text-red-400">#{d.id}</span>
                  <span className="max-w-20 truncate">{d.label || '—'}</span>
                  <button onClick={() => { setEditingId(d.id); setLabelInput(d.label || '') }} className="text-blue-400 ml-0.5"><Edit3 size={10}/></button>
                  <button onClick={() => removeLocalDamage(d.id)} className="text-red-500"><Trash2 size={10}/></button>
                </div>
              ))
            )}
          </div>

          <Button onClick={onRetake} variant="ghost" size="sm" className="flex-shrink-0 px-3 bg-gray-800 text-white border-0 py-2">
            <RotateCcw size={16}/>
          </Button>

          <Button onClick={handleValidate} variant="success" size="sm" className="flex-shrink-0 px-4 py-2 font-bold text-base" disabled={isExporting}>
            {isExporting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"/> : <Check size={18} strokeWidth={3}/>}
          </Button>
        </div>

        {/* Suivant (Massif) */}
        <button
          onClick={handleNextClick}
          disabled={!hasNext}
          className="min-w-[64px] sm:min-w-[80px] bg-amber-400 text-black flex items-center justify-center disabled:bg-gray-800 disabled:text-gray-600 active:bg-amber-300 transition-colors"
        >
          {hasNext ? <ChevronRight size={36} strokeWidth={3} /> : <div className="w-8 h-1 bg-gray-600 rounded-full" />}
        </button>
      </div>
    </div>
  )
})